// Fleet page — vehicle and trailer inventory with VIN/serial scanner
// Same pattern as Cook's Garage vehicles.ts but adds trailers

import { getAllVehicles, addVehicle, getAllTrailers, addTrailer, getInspectionsForVehicle } from '../stores/db.js';
import { decodeVIN } from '../utils/vin-decode.js';
import { showToast } from '../app.js';

export async function renderFleet(container, ctx) {
  const vehicles = await getAllVehicles(ctx.carrierDOT);
  const trailers = await getAllTrailers(ctx.carrierDOT);

  container.innerHTML = `
    <div class="page">
      <div class="page-title">${ctx.role === 'driver' ? 'My Truck' : ctx.role === 'owner-operator' ? 'My Fleet' : 'Fleet'}</div>

      <div style="display: flex; gap: 8px; margin-bottom: 16px;">
        <button class="btn btn-primary btn-small" onclick="window._showAddVehicle()">+ Add Truck</button>
        <button class="btn btn-secondary btn-small" onclick="window._showAddTrailer()">+ Add Trailer</button>
      </div>

      <div id="fleet-list">
        ${vehicles.length === 0 && trailers.length === 0
          ? '<div class="card"><p style="color: #888; text-align: center;">No vehicles yet. Add a truck or trailer to get started.</p></div>'
          : ''}

        ${vehicles.map(v => `
          <div class="card" onclick="window._viewVehicle('${v.vin}')" style="cursor: pointer;">
            <div style="display: flex; justify-content: space-between; align-items: start;">
              <div>
                <div class="card-title">${v.year || ''} ${v.make || ''} ${v.model || ''}</div>
                <div class="card-meta">VIN: ${v.vin}</div>
                ${v.plateNumber ? `<div class="card-meta">Plate: ${v.plateNumber}</div>` : ''}
              </div>
              <div style="text-align: right;">
                <span class="badge ${v.lastInspectionResult === 'pass' ? 'badge-pass' : v.lastInspectionResult === 'fail' ? 'badge-fail' : 'badge-pending'}">
                  ${v.lastInspectionResult || 'No inspection'}
                </span>
                ${v.gvwr && parseInt(v.gvwr) >= 10001 ? '<div class="badge badge-info" style="margin-top: 4px;">CMV</div>' : ''}
              </div>
            </div>
            ${v.mileage ? `<div class="card-meta" style="margin-top: 4px;">${v.mileage.toLocaleString()} miles</div>` : ''}
          </div>
        `).join('')}

        ${trailers.length ? '<div style="margin-top: 16px; font-weight: 600; color: #81d4fa; margin-bottom: 8px;">Trailers</div>' : ''}
        ${trailers.map(t => `
          <div class="card" onclick="window._viewTrailer('${t.id}')" style="cursor: pointer;">
            <div class="card-title">${t.type || 'Trailer'} ${t.make || ''}</div>
            <div class="card-meta">Serial: ${t.serialNumber || 'N/A'} | Plate: ${t.plateNumber || 'N/A'}</div>
            ${t.ownerName ? `<div class="card-meta">Owner: ${t.ownerName}</div>` : ''}
          </div>
        `).join('')}
      </div>

      <!-- Add Vehicle Modal -->
      <div id="add-vehicle-modal" style="display: none;">
        <div class="card" style="border-color: var(--accent);">
          <div class="card-title" style="margin-bottom: 12px;">Add Truck</div>

          <div class="input-group">
            <label>VIN (scan or type)</label>
            <div style="display: flex; gap: 8px;">
              <input type="text" id="add-vin" placeholder="17-character VIN" style="flex: 1;" maxlength="17" oninput="this.value = this.value.toUpperCase()">
              <button class="btn btn-secondary btn-small" onclick="window._scanVIN()" style="width: auto;">Scan</button>
            </div>
          </div>

          <div class="input-group">
            <label>License Plate</label>
            <input type="text" id="add-plate" placeholder="e.g. ABC-1234">
          </div>

          <div class="input-group">
            <label>Current Mileage</label>
            <input type="number" id="add-mileage" placeholder="e.g. 127850">
          </div>

          <div id="vin-decoded" style="display: none; margin-bottom: 12px;"></div>

          <div style="display: flex; gap: 8px;">
            <button class="btn btn-primary" onclick="window._saveVehicle()">Save Truck</button>
            <button class="btn btn-secondary" onclick="window._cancelAdd()">Cancel</button>
          </div>
        </div>
      </div>

      <!-- Add Trailer Modal -->
      <div id="add-trailer-modal" style="display: none;">
        <div class="card" style="border-color: var(--accent);">
          <div class="card-title" style="margin-bottom: 12px;">Add Trailer</div>

          <div class="input-group">
            <label>Trailer Serial Number</label>
            <input type="text" id="add-trailer-serial" placeholder="Manufacturer serial number">
          </div>

          <div class="input-group">
            <label>Trailer ID / Unit Number</label>
            <input type="text" id="add-trailer-unit" placeholder="e.g. TRL-001">
          </div>

          <div class="input-group">
            <label>License Plate</label>
            <input type="text" id="add-trailer-plate" placeholder="Trailer plate number">
          </div>

          <div class="input-group">
            <label>Type</label>
            <select id="add-trailer-type">
              <option value="dry-van">Dry Van</option>
              <option value="flatbed">Flatbed</option>
              <option value="reefer">Refrigerated</option>
              <option value="tanker">Tanker</option>
              <option value="dump">Dump</option>
              <option value="lowboy">Lowboy</option>
              <option value="step-deck">Step Deck</option>
              <option value="chassis">Chassis</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div class="input-group">
            <label>Make / Manufacturer</label>
            <input type="text" id="add-trailer-make" placeholder="e.g. Great Dane, Utility, Wabash">
          </div>

          <div class="input-group">
            <label>Owner Name</label>
            <input type="text" id="add-trailer-owner" placeholder="Who owns this trailer">
          </div>

          <div style="display: flex; gap: 8px;">
            <button class="btn btn-primary" onclick="window._saveTrailer()">Save Trailer</button>
            <button class="btn btn-secondary" onclick="window._cancelAdd()">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  `;

  // ─── Event Handlers ───────────────────────────────────────────

  window._showAddVehicle = () => {
    document.getElementById('add-vehicle-modal').style.display = 'block';
    document.getElementById('add-trailer-modal').style.display = 'none';
    document.getElementById('add-vin').focus();
  };

  window._showAddTrailer = () => {
    document.getElementById('add-trailer-modal').style.display = 'block';
    document.getElementById('add-vehicle-modal').style.display = 'none';
  };

  window._cancelAdd = () => {
    document.getElementById('add-vehicle-modal').style.display = 'none';
    document.getElementById('add-trailer-modal').style.display = 'none';
  };

  window._scanVIN = async () => {
    // Check for BarcodeDetector API
    if ('BarcodeDetector' in window) {
      showToast('Opening scanner...');
      // TODO: Full scanner overlay (reuse Cook's Garage vin-scanner.ts)
    } else {
      showToast('Barcode scanner not available — type VIN manually');
    }
  };

  // Auto-decode VIN on 17 characters
  document.getElementById('add-vin')?.addEventListener('input', async (e) => {
    const vin = e.target.value.trim();
    if (vin.length === 17) {
      const decoded = await decodeVIN(vin);
      if (decoded) {
        const div = document.getElementById('vin-decoded');
        div.style.display = 'block';
        div.innerHTML = `
          <div class="card" style="background: #0d1a0d; border-color: var(--pass);">
            <div style="font-size: 0.85em; color: var(--pass);">VIN Decoded (NHTSA)</div>
            <div style="color: #fff; margin-top: 4px;">${decoded.year} ${decoded.make} ${decoded.model}</div>
            ${decoded.engine ? `<div class="card-meta">Engine: ${decoded.engine}</div>` : ''}
            ${decoded.gvwr ? `<div class="card-meta">GVWR: ${decoded.gvwr} ${parseInt(decoded.gvwr) >= 10001 ? '(CMV)' : ''}</div>` : ''}
            ${decoded.bodyClass ? `<div class="card-meta">Body: ${decoded.bodyClass}</div>` : ''}
          </div>
        `;
      }
    }
  });

  window._saveVehicle = async () => {
    const vin = document.getElementById('add-vin').value.trim().toUpperCase();
    if (!vin || vin.length !== 17) {
      showToast('Enter a valid 17-character VIN');
      return;
    }

    const plate = document.getElementById('add-plate').value.trim();
    const mileage = parseInt(document.getElementById('add-mileage').value) || 0;

    // Decode VIN via NHTSA
    const decoded = await decodeVIN(vin);

    const vehicle = {
      vin,
      plateNumber: plate,
      mileage,
      year: decoded?.year || null,
      make: decoded?.make || '',
      model: decoded?.model || '',
      engine: decoded?.engine || '',
      gvwr: decoded?.gvwr || '',
      bodyClass: decoded?.bodyClass || '',
      carrierDOT: ctx.carrierDOT,
      addedBy: ctx.userName,
      lastInspectionResult: null,
      lastInspectionDate: null,
    };

    await addVehicle(vehicle);
    showToast(`${vehicle.year || ''} ${vehicle.make || 'Vehicle'} added`);
    renderFleet(container, ctx);
  };

  window._saveTrailer = async () => {
    const serial = document.getElementById('add-trailer-serial').value.trim();
    const unit = document.getElementById('add-trailer-unit').value.trim();
    const plate = document.getElementById('add-trailer-plate').value.trim();

    if (!serial && !plate && !unit) {
      showToast('Enter serial number, plate, or unit ID');
      return;
    }

    const trailer = {
      serialNumber: serial,
      unitNumber: unit,
      plateNumber: plate,
      type: document.getElementById('add-trailer-type').value,
      make: document.getElementById('add-trailer-make').value.trim(),
      ownerName: document.getElementById('add-trailer-owner').value.trim(),
      carrierDOT: ctx.carrierDOT,
      addedBy: ctx.userName,
      lastInspectionResult: null,
    };

    await addTrailer(trailer);
    showToast('Trailer added');
    renderFleet(container, ctx);
  };

  window._viewVehicle = (vin) => {
    showToast('Vehicle detail coming soon');
  };

  window._viewTrailer = (id) => {
    showToast('Trailer detail coming soon');
  };
}
